/**
 * Build 5: what a shared register has to tell the person, in one place —
 * the old note waiting to be brought across, a colleague's change that
 * stopped theirs, a save the platform refused, a save that went through.
 */
import { useState } from "react";
import { Button } from "../../design/components";
import { locale, t } from "../../i18n";
import type { RegisterDoc } from "./useRegisterRows";
import s from "./RegisterNotices.module.css";

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString(locale(), { dateStyle: "medium", timeStyle: "short" }) : "");

export function RegisterNotices<D>({ doc, what }: { doc: RegisterDoc<D>; what: string }) {
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ ok: boolean; text: string } | null>(null);

  return (
    <div className={s.wrap} data-testid="register-notices">
      {doc.waiting && (
        <div className={s.offer} role="status" data-testid="register-waiting">
          <p>
            <b>{t("This register is still kept the old way.")}</b>{" "}
            {t("The {what} is saved as one document, so two people saving at once overwrite each other.", { what })}{" "}
            {t("Bring its {n} rows across once, and everyone saves row by row.", { n: doc.waiting.rows })}
          </p>
          {doc.canWrite ? (
            <Button
              variant="primary"
              disabled={busy}
              data-testid="register-bring-across"
              onClick={async () => {
                setBusy(true);
                setSaid(await doc.bringAcross());
                setBusy(false);
              }}
            >
              {busy ? t("Bringing across…") : t("Bring it across")}
            </Button>
          ) : (
            <p className={s.dim}>{t("Someone who can change this register must bring it across.")}</p>
          )}
        </div>
      )}
      {said && (
        <p className={said.ok ? s.ok : s.bad} role="status">
          {said.text}
        </p>
      )}
      {doc.conflict && (
        <div className={s.conflict} role="alert" data-testid="register-conflict">
          <p>
            <b>
              {doc.conflict.deleted
                ? t("{who} deleted {what} after you opened it.", { who: doc.conflict.by ?? t("Someone else"), what: doc.conflict.what })
                : t("{who} changed {what} after you opened it.", { who: doc.conflict.by ?? t("Someone else"), what: doc.conflict.what })}
            </b>{" "}
            {doc.conflict.at ? t("Saved {when}.", { when: when(doc.conflict.at) }) + " " : ""}
            {t("Your change was not saved. Their version is shown now; make your change again if it is still needed.")}
          </p>
          <Button onClick={doc.dismiss} data-testid="register-conflict-ok">
            {t("OK")}
          </Button>
        </div>
      )}
      {doc.failed && !doc.conflict && (
        <p className={s.bad} role="alert" data-testid="register-failed">
          {t("Not saved: {why}", { why: doc.failed })}
        </p>
      )}
      {doc.error && (
        <p className={s.bad} role="alert" data-testid="register-error">
          {t("The register could not be read: {why}", { why: doc.error })}
        </p>
      )}
      {doc.savedTo && !doc.conflict && !doc.failed && <p className={s.ok}>{doc.savedTo}</p>}
    </div>
  );
}
